import { useState, useCallback, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Sparkles, Upload, Download, Trash2, FolderOpen, 
  Pause, Play, Square, RefreshCw, Archive, Check, X, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { EmbroideryConverter } from '@/components/EmbroideryConverter';
import { useBulkImageEnhancement } from '@/hooks/useBulkImageEnhancement';
import SEO from '@/components/SEO';

export default function PublicImageEnhancer() {
  const {
    images,
    stats,
    isProcessing,
    isPaused,
    isCreatingZip,
    zipProgress,
    addImages,
    removeImage,
    clearAll,
    processImages,
    pauseProcessing,
    resumeProcessing,
    stopProcessing,
    retryFailed,
    downloadAllAsZip
  } = useBulkImageEnhancement();

  const [enhancementType, setEnhancementType] = useState<'dpi' | 'quality' | 'both'>('both');
  const [dpiTarget, setDpiTarget] = useState(300);
  const [qualityLevel, setQualityLevel] = useState<'standard' | 'hd' | 'ultra'>('hd');
  
  // Watermark settings
  const [enableWatermark, setEnableWatermark] = useState(false);
  const [watermarkText, setWatermarkText] = useState('© My Brand');
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'>('bottom-right');
  const [watermarkOpacity, setWatermarkOpacity] = useState(50);
  const [watermarkSize, setWatermarkSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [watermarkColor, setWatermarkColor] = useState('#FFFFFF');
  const [watermarkBackground, setWatermarkBackground] = useState(true);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;
    addImages(files);
  }, [addImages]);

  const handleEnhance = () => {
    processImages({
      enhancementType,
      dpiTarget,
      qualityLevel,
      watermark: enableWatermark ? {
        text: watermarkText,
        position: watermarkPosition,
        opacity: watermarkOpacity,
        size: watermarkSize,
        color: watermarkColor,
        background: watermarkBackground
      } : undefined
    });
  };

  const handleRetryFailed = () => {
    retryFailed({
      enhancementType,
      dpiTarget,
      qualityLevel,
      watermark: enableWatermark ? {
        text: watermarkText,
        position: watermarkPosition,
        opacity: watermarkOpacity,
        size: watermarkSize,
        color: watermarkColor,
        background: watermarkBackground
      } : undefined
    });
  };

  const completedCount = images.filter(img => img.status === 'completed').length;
  const failedCount = images.filter(img => img.status === 'failed').length;
  const pendingCount = images.filter(img => img.status === 'pending').length;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-900/20 via-background to-background">
      <SEO
        title="Free AI Image Enhancer | Upscale & Improve Photos"
        description="Enhance your images for free with AI. Increase resolution, improve quality, and add DPI for print-ready images. No signup required."
        keywords="ai image enhancer, upscale images, image quality boost, dpi enhancement, photo upscaler free"
        url="/tools/image-enhancer"
      />
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12 max-w-6xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            Free Image Tools
          </h1>
          <p className="text-lg text-muted-foreground">
            Professional image enhancement and embroidery conversion tools
          </p>
        </div>

        <Tabs defaultValue="enhance" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="enhance">Image Enhancement</TabsTrigger>
            <TabsTrigger value="embroidery">Embroidery Conversion</TabsTrigger>
          </TabsList>

          <TabsContent value="enhance">
            <Card className="p-8 bg-gradient-to-br from-purple-500/5 to-blue-500/5 border-purple-500/20">
              {/* Upload Area */}
              <div
                className="border-2 border-dashed border-purple-500/30 rounded-lg p-8 mb-8 text-center hover:border-purple-500/50 transition-colors"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFileUpload(e.dataTransfer.files);
                }}
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-purple-400" />
                <p className="text-lg mb-2">Drop files/folder here or use buttons below</p>
                <p className="text-sm text-muted-foreground mb-4">
                  PNG, JPG, WebP (Max 250 images, 10MB each)
                </p>
                
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Select Files
                  </Button>
                  <Button
                    onClick={() => folderInputRef.current?.click()}
                    variant="outline"
                    className="gap-2"
                  >
                    <FolderOpen className="w-4 h-4" />
                    Select Folder
                  </Button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files)}
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  // @ts-ignore - webkitdirectory is not in types
                  webkitdirectory=""
                  directory=""
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files)}
                />
              </div>

              {/* Stats Dashboard */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-foreground">{images.length}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-500">{pendingCount}</div>
                    <div className="text-xs text-muted-foreground">Pending</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-500">{completedCount}</div>
                    <div className="text-xs text-muted-foreground">Completed</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-500">{failedCount}</div>
                    <div className="text-xs text-muted-foreground">Failed</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-purple-400 flex items-center justify-center gap-1">
                      <Clock className="w-4 h-4" />
                      {stats.estimatedTimeRemaining}
                    </div>
                    <div className="text-xs text-muted-foreground">Est. Time</div>
                  </div>
                </div>
              )}

              {/* Processing Progress */}
              {isProcessing && (
                <div className="mb-8 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      Processing batch {stats.currentBatch} of {stats.totalBatches}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {stats.processed} / {stats.total} images
                    </span>
                  </div>
                  <Progress 
                    value={(stats.processed / stats.total) * 100} 
                    className="h-3"
                  />
                </div>
              )}

              {/* Enhancement Options */}
              <div className="space-y-8 mb-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <Label className="mb-3 block">Enhancement Type</Label>
                    <RadioGroup value={enhancementType} onValueChange={(v) => setEnhancementType(v as any)}>
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="dpi" id="pub-dpi" />
                        <Label htmlFor="pub-dpi" className="cursor-pointer">DPI Enhancement</Label>
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="quality" id="pub-quality" />
                        <Label htmlFor="pub-quality" className="cursor-pointer">Quality Boost</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="both" id="pub-both" />
                        <Label htmlFor="pub-both" className="cursor-pointer">Both (Recommended)</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {(enhancementType === 'dpi' || enhancementType === 'both') && (
                    <div>
                      <Label className="mb-3 block">Target DPI: {dpiTarget}</Label>
                      <Slider
                        value={[dpiTarget]}
                        onValueChange={(v) => setDpiTarget(v[0])}
                        min={150}
                        max={600}
                        step={150}
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>150</span>
                        <span>300</span>
                        <span>600</span>
                      </div>
                    </div>
                  )}
                  
                  {(enhancementType === 'quality' || enhancementType === 'both') && (
                    <div>
                      <Label className="mb-3 block">Quality Level</Label>
                      <RadioGroup value={qualityLevel} onValueChange={(v) => setQualityLevel(v as any)}>
                        <div className="flex items-center space-x-2 mb-2">
                          <RadioGroupItem value="standard" id="quality-standard" />
                          <Label htmlFor="quality-standard" className="cursor-pointer">Standard</Label>
                        </div>
                        <div className="flex items-center space-x-2 mb-2">
                          <RadioGroupItem value="hd" id="quality-hd" />
                          <Label htmlFor="quality-hd" className="cursor-pointer">HD (Recommended)</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="ultra" id="quality-ultra" />
                          <Label htmlFor="quality-ultra" className="cursor-pointer">Ultra</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}
                </div>

                {/* Watermark Section */}
                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-lg font-semibold">Watermark Settings</Label>
                      <p className="text-sm text-muted-foreground mt-1">Add custom watermark to your enhanced images</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="enable-watermark" className="cursor-pointer">Enable</Label>
                      <input
                        id="enable-watermark"
                        type="checkbox"
                        checked={enableWatermark}
                        onChange={(e) => setEnableWatermark(e.target.checked)}
                        className="w-5 h-5 cursor-pointer accent-purple-500"
                      />
                    </div>
                  </div>

                  {enableWatermark && (
                    <div className="grid md:grid-cols-2 gap-6 mt-4">
                      {/* Watermark Text */}
                      <div className="md:col-span-2">
                        <Label className="mb-2 block">Watermark Text</Label>
                        <input
                          type="text"
                          value={watermarkText}
                          onChange={(e) => setWatermarkText(e.target.value)}
                          placeholder="Enter watermark text"
                          className="w-full px-4 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      {/* Position */}
                      <div>
                        <Label className="mb-3 block">Position</Label>
                        <RadioGroup value={watermarkPosition} onValueChange={(v) => setWatermarkPosition(v as any)}>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="top-left" id="pos-tl" />
                              <Label htmlFor="pos-tl" className="cursor-pointer text-xs">Top Left</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="top-center" id="pos-tc" />
                              <Label htmlFor="pos-tc" className="cursor-pointer text-xs">Top Center</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="top-right" id="pos-tr" />
                              <Label htmlFor="pos-tr" className="cursor-pointer text-xs">Top Right</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="center-left" id="pos-cl" />
                              <Label htmlFor="pos-cl" className="cursor-pointer text-xs">Center Left</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="center" id="pos-c" />
                              <Label htmlFor="pos-c" className="cursor-pointer text-xs">Center</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="center-right" id="pos-cr" />
                              <Label htmlFor="pos-cr" className="cursor-pointer text-xs">Center Right</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="bottom-left" id="pos-bl" />
                              <Label htmlFor="pos-bl" className="cursor-pointer text-xs">Bottom Left</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="bottom-center" id="pos-bc" />
                              <Label htmlFor="pos-bc" className="cursor-pointer text-xs">Bottom Center</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="bottom-right" id="pos-br" />
                              <Label htmlFor="pos-br" className="cursor-pointer text-xs">Bottom Right</Label>
                            </div>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Size */}
                      <div>
                        <Label className="mb-3 block">Font Size</Label>
                        <RadioGroup value={watermarkSize} onValueChange={(v) => setWatermarkSize(v as any)}>
                          <div className="flex items-center space-x-2 mb-2">
                            <RadioGroupItem value="small" id="size-small" />
                            <Label htmlFor="size-small" className="cursor-pointer">Small</Label>
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <RadioGroupItem value="medium" id="size-medium" />
                            <Label htmlFor="size-medium" className="cursor-pointer">Medium</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="large" id="size-large" />
                            <Label htmlFor="size-large" className="cursor-pointer">Large</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Opacity */}
                      <div>
                        <Label className="mb-3 block">Opacity: {watermarkOpacity}%</Label>
                        <Slider
                          value={[watermarkOpacity]}
                          onValueChange={(v) => setWatermarkOpacity(v[0])}
                          min={10}
                          max={100}
                          step={5}
                        />
                      </div>

                      {/* Color */}
                      <div>
                        <Label className="mb-2 block">Text Color</Label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={watermarkColor}
                            onChange={(e) => setWatermarkColor(e.target.value)}
                            className="w-16 h-10 cursor-pointer rounded border border-border"
                          />
                          <input
                            type="text"
                            value={watermarkColor}
                            onChange={(e) => setWatermarkColor(e.target.value)}
                            placeholder="#FFFFFF"
                            className="flex-1 px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>

                      {/* Background */}
                      <div className="flex items-center gap-3">
                        <input
                          id="watermark-bg"
                          type="checkbox"
                          checked={watermarkBackground}
                          onChange={(e) => setWatermarkBackground(e.target.checked)}
                          className="w-5 h-5 cursor-pointer accent-purple-500"
                        />
                        <div>
                          <Label htmlFor="watermark-bg" className="cursor-pointer">Add Background</Label>
                          <p className="text-xs text-muted-foreground">Semi-transparent background for better visibility</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mb-8 flex-wrap">
                {!isProcessing ? (
                  <Button
                    onClick={handleEnhance}
                    disabled={images.length === 0 || pendingCount === 0}
                    className="flex-1 min-w-[200px]"
                    size="lg"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Enhance {pendingCount} Image{pendingCount !== 1 ? 's' : ''}
                  </Button>
                ) : (
                  <>
                    {isPaused ? (
                      <Button onClick={resumeProcessing} variant="outline" size="lg" className="gap-2">
                        <Play className="w-5 h-5" />
                        Resume
                      </Button>
                    ) : (
                      <Button onClick={pauseProcessing} variant="outline" size="lg" className="gap-2">
                        <Pause className="w-5 h-5" />
                        Pause
                      </Button>
                    )}
                    <Button onClick={stopProcessing} variant="destructive" size="lg" className="gap-2">
                      <Square className="w-5 h-5" />
                      Stop
                    </Button>
                  </>
                )}

                {failedCount > 0 && !isProcessing && (
                  <Button onClick={handleRetryFailed} variant="outline" size="lg" className="gap-2">
                    <RefreshCw className="w-5 h-5" />
                    Retry {failedCount} Failed
                  </Button>
                )}

                <Button
                  onClick={downloadAllAsZip}
                  disabled={completedCount === 0 || isCreatingZip}
                  variant="secondary"
                  size="lg"
                  className="gap-2"
                >
                  {isCreatingZip ? (
                    <>
                      <Archive className="w-5 h-5 animate-pulse" />
                      Creating ZIP... {Math.round(zipProgress)}%
                    </>
                  ) : (
                    <>
                      <Archive className="w-5 h-5" />
                      Download All as ZIP ({completedCount})
                    </>
                  )}
                </Button>

                {images.length > 0 && !isProcessing && (
                  <Button onClick={clearAll} variant="ghost" size="lg" className="gap-2">
                    <Trash2 className="w-5 h-5" />
                    Clear All
                  </Button>
                )}
              </div>

              {/* Image Grid */}
              {images.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Images ({images.length}/250)</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 max-h-[600px] overflow-y-auto p-2">
                    {images.map((image) => (
                      <div 
                        key={image.id} 
                        className="relative group bg-card border border-border rounded-lg overflow-hidden"
                      >
                        <div className="aspect-square">
                          <img
                            src={image.status === 'completed' && image.enhancedUrl ? image.enhancedUrl : image.preview}
                            alt={image.file.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Status overlay */}
                        {image.status === 'processing' && (
                          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-2">
                            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-white text-xs">Processing...</span>
                          </div>
                        )}
                        
                        {image.status === 'completed' && (
                          <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        
                        {image.status === 'failed' && (
                          <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center">
                            <div className="bg-red-500 rounded-full p-1">
                              <X className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        )}

                        {/* Remove button */}
                        <Button
                          onClick={() => removeImage(image.id)}
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 left-2 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={isProcessing && image.status === 'processing'}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>

                        {/* File name tooltip */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-white text-xs truncate">{image.file.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacy Notice */}
              <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-center text-muted-foreground">
                  🔒 Your privacy matters: All images are processed securely and automatically deleted after 24 hours.
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="embroidery">
            <EmbroideryConverter />
          </TabsContent>
        </Tabs>
      </main>
      
      <Footer />
    </div>
  );
}
